import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import { api, MACHINE_TYPES, MACHINE_STATUSES } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nProvider";
import { toast } from "sonner";
import { Plus, Wrench, MapPin, Leaf, Pencil } from "lucide-react";
import StatusBadge from "../components/StatusBadge";

function statusKey(s) {
  return { tillganglig: "Tillganglig", bokad: "Bokad", utlamnad: "Utlamnad", service: "Service", trasig: "Trasig" }[s] || "Tillganglig";
}

const EMPTY_FORM = {
  namn: "", typ: "borrmaskin", plats: "", ansvarig: "", status: "tillganglig",
  enable_rental_reporting: false, hourly_rate: "",
};

export default function Machines() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ typ: "", status: "" });
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.typ) params.typ = filter.typ;
      if (filter.status) params.status = filter.status;
      const { data } = await api.get("/machines", { params });
      setItems(data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (user) load(); }, [user, filter.typ, filter.status]);

  // Pre-load edit form when ?edit=<machine_id> is present (deep link from MachineDetail)
  useEffect(() => {
    const editParam = searchParams.get("edit");
    if (!editParam || !user) return;
    api.get(`/machines/${editParam}`)
      .then((r) => startEdit(r.data))
      .catch(() => toast.error(t("dash.failed")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, user]);

  if (!user) {
    return <Layout><div className="max-w-[1440px] mx-auto p-10 label-tech">{t("add.loginRequired")}</div></Layout>;
  }

  const startEdit = (m) => {
    setEditingId(m.id);
    setForm({
      namn: m.namn || "",
      typ: m.typ || "borrmaskin",
      plats: m.plats || "",
      ansvarig: m.ansvarig || "",
      status: m.status || "tillganglig",
      enable_rental_reporting: !!m.enable_rental_reporting,
      hourly_rate: m.hourly_rate ?? "",
    });
    setShowForm(true);
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    if (searchParams.get("edit")) {
      const next = new URLSearchParams(searchParams);
      next.delete("edit");
      setSearchParams(next, { replace: true });
    }
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        hourly_rate: form.hourly_rate === "" ? null : Number(form.hourly_rate),
      };
      if (editingId) {
        await api.patch(`/machines/${editingId}`, payload);
        toast.success(t("mach.updated"));
      } else {
        await api.post("/machines", payload);
        toast.success(t("mach.created"));
      }
      cancelForm();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("dash.failed"));
    }
  };

  const canCreate = user.role === "seller" || user.role === "admin";
  const canEdit = (m) => user.role === "admin" || user.user_id === m.owner_id;

  return (
    <Layout>
      <div className="max-w-[1440px] mx-auto px-6 pt-10 pb-16">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="label-tech">{t("mach.label")}</div>
            <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tighter mt-2">{t("mach.title")}</h1>
          </div>
          {canCreate && (
            <button onClick={() => { if (showForm) { cancelForm(); } else { setShowForm(true); } }} className="btn-primary" data-testid="new-machine-btn">
              <Plus size={14} /> {t("mach.new")}
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="mt-6 flex flex-wrap gap-3">
          <select className="input-tech max-w-[200px]" value={filter.typ} onChange={(e) => setFilter({ ...filter, typ: e.target.value })} data-testid="machine-filter-type">
            <option value="">{t("mach.typ")}: —</option>
            {MACHINE_TYPES.map((t2) => <option key={t2} value={t2}>{t2}</option>)}
          </select>
          <select className="input-tech max-w-[200px]" value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })} data-testid="machine-filter-status">
            <option value="">{t("mach.status")}: —</option>
            {MACHINE_STATUSES.map((s) => <option key={s} value={s}>{t(`mach.status${statusKey(s)}`)}</option>)}
          </select>
        </div>

        {showForm && (
          <form onSubmit={create} className="mt-6 border border-[var(--ink-200)] bg-[var(--bone)] p-5 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="machine-form">
            <div className="md:col-span-3 label-tech">{editingId ? t("mach.editTitle") : t("mach.newTitle")}</div>
            <div><div className="label-tech mb-2">{t("mach.namn")}</div><input className="input-tech" value={form.namn} onChange={(e) => setForm({ ...form, namn: e.target.value })} required data-testid="machine-namn-input" /></div>
            <div><div className="label-tech mb-2">{t("mach.typ")}</div>
              <select className="input-tech" value={form.typ} onChange={(e) => setForm({ ...form, typ: e.target.value })} data-testid="machine-typ-select">
                {MACHINE_TYPES.map((tt) => <option key={tt} value={tt}>{tt}</option>)}
              </select>
            </div>
            <div><div className="label-tech mb-2">{t("mach.plats")}</div><input className="input-tech" value={form.plats} onChange={(e) => setForm({ ...form, plats: e.target.value })} data-testid="machine-plats-input" /></div>
            <div><div className="label-tech mb-2">{t("mach.ansvarig")}</div><input className="input-tech" value={form.ansvarig} onChange={(e) => setForm({ ...form, ansvarig: e.target.value })} data-testid="machine-ansvarig-input" /></div>
            {editingId && (
              <div><div className="label-tech mb-2">{t("mach.status")}</div>
                <select className="input-tech" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="machine-status-select">
                  {MACHINE_STATUSES.map((s) => <option key={s} value={s}>{t(`mach.status${statusKey(s)}`)}</option>)}
                </select>
              </div>
            )}
            {/* Rental reporting */}
            <div className="md:col-span-3 border-t border-[var(--ink-200)] pt-4 mt-2">
              <label className="flex items-start gap-3 cursor-pointer" data-testid="machine-rental-toggle">
                <input
                  type="checkbox"
                  checked={form.enable_rental_reporting}
                  onChange={(e) => setForm({ ...form, enable_rental_reporting: e.target.checked })}
                  className="mt-1"
                  data-testid="machine-rental-checkbox"
                />
                <div>
                  <div className="font-medium text-sm">{t("rental.toggle")}</div>
                  <div className="text-xs text-[var(--ink-500)] mt-1 max-w-xl">{t("rental.toggleHelp")}</div>
                </div>
              </label>
              {form.enable_rental_reporting && (
                <div className="mt-3 max-w-xs">
                  <div className="label-tech mb-2">{t("rental.hourlyRate")}</div>
                  <input
                    type="number"
                    step="1"
                    placeholder="150"
                    className="input-tech"
                    value={form.hourly_rate}
                    onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })}
                    data-testid="machine-hourly-rate-input"
                  />
                </div>
              )}
            </div>
            <div className="md:col-span-3 flex justify-end gap-2">
              <button type="button" className="btn-ghost" onClick={cancelForm}>{t("admin.orgs.cancel")}</button>
              <button type="submit" className="btn-primary" data-testid="machine-submit-btn">{t("admin.orgs.save")}</button>
            </div>
          </form>
        )}

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--ink-200)] border border-[var(--ink-200)]">
          {loading ? (
            <div className="bg-[var(--surface)] p-6 label-tech">…</div>
          ) : items.length === 0 ? (
            <div className="bg-[var(--surface)] p-10 label-tech md:col-span-2 lg:col-span-3">{t("mach.empty")}</div>
          ) : items.map((m) => (
            <Link key={m.id} to={`/machines/${m.id}`} className="bg-[var(--surface)] p-5 hover:bg-[var(--surface-alt)] transition-colors block" data-testid={`machine-card-${m.id}`}>
              <div className="flex items-center gap-2 label-tech"><Wrench size={12} /> {m.typ}</div>
              <div className="font-display text-lg mt-2">{m.namn}</div>
              <div className="mt-2 flex items-center gap-3 text-sm text-[var(--ink-700)]">
                <span className="inline-flex items-center gap-1"><MapPin size={12} /> {m.plats || "—"}</span>
                <StatusBadge status={m.status} />
              </div>
              <div className="mt-3 label-tech inline-flex items-center gap-1"><Leaf size={12} /> ~{m.generisk_co2e_kop || 0} kg CO2e</div>
            </Link>
          ))}
        </div>
      </div>
    </Layout>
  );
}
